/**
 * Google Calendar as a collection.
 *
 * Every call here goes straight from the browser to googleapis.com, which
 * sends CORS headers for the Calendar API. No event title, guest list or
 * description ever reaches the Eventera backend.
 *
 * Two things make it the odd one out next to Gmail. Its items are *spans* --
 * a meeting occupies an hour and a holiday a whole day, and `until` is what
 * puts them on the timeline at their real width. And its rows arrive complete:
 * `events.list` returns the description with the event, so there is no
 * `fetchDetail` half to fetch when one is opened.
 */

import { SiGooglecalendar } from "react-icons/si";

import { maybeHtmlToText } from "./html";
import { mapPool } from "./pool";
import {
  ExternalItem,
  ExternalProviderModule,
  ProviderFetchContext,
} from "./types";

const API = "https://www.googleapis.com/calendar/v3";

/**
 * `calendar.readonly` covers both halves of the job: which calendars the
 * account has, and what is in them. `calendar.events.readonly` is narrower but
 * cannot answer the first, which would leave this module guessing at calendar
 * ids rather than reading them.
 */
export const CALENDAR_SCOPE =
  "https://www.googleapis.com/auth/calendar.readonly";

/**
 * How many calendars are read at once.
 *
 * Unlike Gmail's pool this is not one request per row -- it is one per
 * calendar, and most accounts have three or four. It is the account subscribed
 * to every holiday, birthday and football calendar going that this exists for.
 */
const FETCH_CONCURRENCY = 6;

/** Per request. The API's own default is 250 and its maximum 2500. */
const PAGE_SIZE = 250;

interface CalendarListEntry {
  id: string;
  summary?: string;
  primary?: boolean;
  /** Whether the account shows this calendar in Google's own UI. */
  selected?: boolean;
  deleted?: boolean;
}

interface CalendarListResponse {
  items?: CalendarListEntry[];
}

/** One end of an event: `dateTime` for a timed one, `date` for all-day. */
interface EventDateTime {
  date?: string;
  dateTime?: string;
  timeZone?: string;
}

interface CalendarEvent {
  id: string;
  status?: string;
  summary?: string;
  description?: string;
  location?: string;
  htmlLink?: string;
  hangoutLink?: string;
  start?: EventDateTime;
  end?: EventDateTime;
  attendees?: { self?: boolean; responseStatus?: string }[];
}

interface EventsResponse {
  items?: CalendarEvent[];
  nextPageToken?: string;
}

/** The only two things any Calendar call needs from its caller's context. */
type CalendarAuth = Pick<ProviderFetchContext, "accessToken" | "signal">;

async function calendarFetch<T>(
  path: string,
  params: URLSearchParams,
  auth: CalendarAuth,
): Promise<T> {
  const response = await fetch(`${API}${path}?${params}`, {
    headers: { Authorization: `Bearer ${auth.accessToken}` },
    signal: auth.signal,
  });
  if (!response.ok) {
    // 401/403 reach the loader as a plain error and are reported per source;
    // the token broker is what turns a dead *grant* into a reconnect prompt.
    throw new Error(`Google Calendar request failed (${response.status})`);
  }
  return (await response.json()) as T;
}

/**
 * One end of an event, as a moment.
 *
 * `dateTime` carries its own UTC offset and needs nothing. `date` is an
 * all-day event, and is deliberately read as *local* midnight:
 * `new Date("2026-08-31")` is midnight UTC, which files a whole-day event on
 * the day before for everyone west of Greenwich.
 */
function eventMoment(when: EventDateTime | undefined): Date | null {
  if (when?.dateTime) {
    const parsed = new Date(when.dateTime);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (when?.date) {
    const [year, month, day] = when.date.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }
  return null;
}

function toItem(event: CalendarEvent): ExternalItem | null {
  // `showDeleted` is off by default, so a cancelled row reaching here is a
  // cancelled *instance* of a recurring event -- the one occurrence deleted
  // out of a series that otherwise still stands.
  if (event.status === "cancelled") return null;
  // A meeting turned down did not happen to this person. It is still on the
  // calendar because the invitation is, which is not the same thing.
  const self = event.attendees?.find((attendee) => attendee.self);
  if (self?.responseStatus === "declined") return null;

  const at = eventMoment(event.start);
  if (!at) return null;

  let until = eventMoment(event.end);
  // An all-day event ends on the *exclusive* next date: a one-day holiday runs
  // 2026-08-31 to 2026-09-01. Backing off a second keeps it inside its own day
  // rather than reaching into the next one.
  if (until && event.end?.date) until = new Date(until.getTime() - 1000);

  const description = event.description
    ? maybeHtmlToText(event.description)
    : "";

  return {
    externalId: event.id,
    title: event.summary || "(no title)",
    // Location first: it is the line that fits in a list row, and the
    // description is the body the preview pane opens to.
    content: [event.location?.trim(), description].filter(Boolean).join("\n\n"),
    at,
    until: until ?? undefined,
    // The API prints the permalink, so there is nothing to construct and no
    // `itemUrl` on this module.
    href: event.htmlLink,
    // Unlike the permalink, a Meet link is part of the event rather than a way
    // back to it, so it belongs in `url` and is honestly counted as a link.
    url: event.hangoutLink
      ? [{ label: "Meeting link", url: event.hangoutLink }]
      : [],
  };
}

/**
 * Which calendars to read.
 *
 * `provider_config.calendarId` names exactly one -- the seam a per-calendar
 * subcollection would be built on -- and without it the account's own choice
 * is honoured: the calendars it has ticked in Google's UI. One the user has
 * hidden there is one they have already said they do not want to look at.
 */
async function calendarIds(ctx: ProviderFetchContext): Promise<string[]> {
  const configured = ctx.config.calendarId;
  if (typeof configured === "string" && configured.trim()) {
    return [configured.trim()];
  }

  const page = await calendarFetch<CalendarListResponse>(
    "/users/me/calendarList",
    new URLSearchParams({ minAccessRole: "reader", maxResults: "250" }),
    ctx,
  );
  const usable = (page.items ?? []).filter(
    (entry) => !entry.deleted && (entry.primary || entry.selected),
  );
  // An account whose list has nothing ticked still has a primary calendar, and
  // that is the one a person means by "my calendar".
  return usable.length > 0 ? usable.map((entry) => entry.id) : ["primary"];
}

async function fetchEvents(
  calendarId: string,
  ctx: ProviderFetchContext,
): Promise<ExternalItem[]> {
  const items: ExternalItem[] = [];
  let pageToken: string | undefined;

  while (items.length < ctx.limit) {
    const params = new URLSearchParams({
      timeMin: ctx.from.toISOString(),
      timeMax: ctx.to.toISOString(),
      // Recurrences expanded into the occurrences that fall inside the range:
      // a timeline shows what happened, not the rule that made it happen. It
      // is also what orderBy=startTime requires.
      singleEvents: "true",
      orderBy: "startTime",
      maxResults: String(Math.min(PAGE_SIZE, ctx.limit - items.length)),
    });
    if (pageToken) params.set("pageToken", pageToken);

    const page = await calendarFetch<EventsResponse>(
      `/calendars/${encodeURIComponent(calendarId)}/events`,
      params,
      ctx,
    );
    for (const event of page.items ?? []) {
      const item = toItem(event);
      if (item) items.push(item);
    }

    pageToken = page.nextPageToken;
    if (!pageToken) break;
  }

  return items;
}

export const gcalendar: ExternalProviderModule = {
  key: "gcalendar",
  systemId: "google",
  title: "Google Calendar",
  description: "Your events, at their real length on the timeline. Read-only.",
  Icon: SiGooglecalendar,
  scopes: [CALENDAR_SCOPE],
  // Deliberately none. A calendar's natural partition is the account's own
  // list of calendars, which exists only after the grant does and is different
  // for every account -- nothing a static template can name. One collection
  // reads them all, and because it has no children it fetches for itself.
  naturalChildren: [],
  limits: {
    // A year of somebody's calendar is a few hundred rows and reads perfectly
    // well grouped by month; a year of their mail is tens of thousands. That
    // difference, not the API's, is why this cap and Gmail's are an order of
    // magnitude apart.
    maxSpanDays: 366,
    maxItems: 500,
  },
  transport: "client",

  async fetchItems(ctx: ProviderFetchContext): Promise<ExternalItem[]> {
    const ids = await calendarIds(ctx);
    const perCalendar = await mapPool(
      ids,
      FETCH_CONCURRENCY,
      (id) => fetchEvents(id, ctx),
      ctx.signal,
    );

    // Two calendars can hold the same event: an invitation keeps the
    // organizer's id on every copy, so a meeting on both your own calendar and
    // a team one arrives twice under one id. Keyed by that id, the second copy
    // is the same meeting rather than a second one.
    const byId = new Map<string, ExternalItem>();
    for (const items of perCalendar) {
      for (const item of items) {
        if (!byId.has(item.externalId)) byId.set(item.externalId, item);
      }
    }

    // Merged from several calendars, so each one's ordering has stopped
    // meaning anything; sorted by when it happened, which is what the timeline
    // reads in. The cap then keeps the front of the range, and the band says
    // when it was hit.
    return [...byId.values()]
      .sort((a, b) => a.at.getTime() - b.at.getTime())
      .slice(0, ctx.limit);
  },
};
